import React, { useEffect, useState, useMemo } from 'react';
import {
    Autocomplete, Box, Chip, CircularProgress, Divider, Grid,
    IconButton, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon,
    Download as DownloadIcon,
    ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE } from '../../../config/constants';
import axiosInstance from '../../../axiosInstance';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';

const SowForm = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(true);
    const [selectedJobCode, setSelectedJobCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [sowData, setSowData] = useState(null);
    const [sowItems, setSowItems] = useState([]);
    const [jobInfo, setJobInfo] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [changeOrders, setChangeOrders] = useState([]);
    const [coDocuments, setCoDocuments] = useState([]);
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);
    const [docLoadingId, setDocLoadingId] = useState(null);

    const sortedJobs = useMemo(() =>
        jobs.map(j => ({ ...j, label: `${j.Job} - ${j.JobName}` }))
            .sort((a, b) => (parseInt(b.Job) || 0) - (parseInt(a.Job) || 0)),
    [jobs]);

    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();
                setJobs(await db.transaction(JOB_STORE, 'readonly').objectStore(JOB_STORE).getAll() || []);
            } catch (e) { console.error('Error loading jobs:', e); }
            finally { setJobsLoading(false); }
        })();
    }, []);

    useEffect(() => {
        if (!selectedJobCode) { setSowData(null); setSowItems([]); setJobInfo(null); setDocuments([]); setChangeOrders([]); setCoDocuments([]); return; }
        (async () => {
            setLoading(true);
            try {
                const { data } = await axiosInstance.get(`/scope-of-work/read/${encodeURIComponent(selectedJobCode)}`);
                setSowData(data.sow || null);
                setSowItems(data.sowItems || []);
                setJobInfo(data.job || null);
                setDocuments(data.documents || []);
                setChangeOrders(data.changeOrders || []);
                setCoDocuments(data.coDocuments || []);
            } catch (e) {
                console.error('Error loading SOW:', e);
                setSowData(null); setJobInfo(null); setDocuments([]); setChangeOrders([]); setCoDocuments([]);
            } finally { setLoading(false); }
        })();
    }, [selectedJobCode]);

    const handleDocAction = async (doc, preview = false) => {
        const path = doc.docLocation || '';
        if (!path) return;
        const ext = path.split('.').pop()?.toLowerCase() || '';
        setDocLoadingId(doc.id);
        try {
            const response = await axiosInstance.post('/tool-doc/download', { docLocation: path }, { responseType: 'blob' });
            const blob = response.data;
            const fileName = path.split(/[/\\]/).pop() || 'document';
            if (preview && isPreviewableExt(ext)) {
                setPdfBlob(blob); setPdfFilename(fileName); setPdfPreviewOpen(true);
            } else {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = fileName;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                setTimeout(() => window.URL.revokeObjectURL(url), 5000);
            }
        } catch (e) { console.error('Doc action failed:', e); }
        finally { setDocLoadingId(null); }
    };

    const previewableExts = ['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff', 'gif', 'bmp'];
    const isPreviewableExt = (ext) => previewableExts.includes(ext);

    const hasData = sowData || sowItems.length > 0 || documents.length > 0;

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start"><ArrowBackIcon /></IconButton>
                    <Typography variant="h6" fontWeight={600}>Scope of Work</Typography>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    {/* Job selector */}
                    <Autocomplete options={sortedJobs} getOptionLabel={o => o.label || ''} loading={jobsLoading}
                        value={sortedJobs.find(j => j.Job === selectedJobCode) || null}
                        onChange={(_, v) => setSelectedJobCode(v?.Job || '')}
                        isOptionEqualToValue={(o, v) => o.Job === v?.Job}
                        renderInput={p => <TextField {...p} label="Select Job" size="small" />}
                        sx={{ mb: 2 }} />

                    {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

                    {!loading && selectedJobCode && !hasData && (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No Scope of Work found for this job</Typography>
                    )}

                    {!loading && jobInfo && hasData && (
                        <Box sx={{ backgroundColor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', p: 2 }}>
                            {/* Job info row */}
                            <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                <Grid item xs={3}><Typography variant="caption" color="text.secondary">Job</Typography><Typography variant="body2" fontWeight={600}>{jobInfo.job}</Typography></Grid>
                                <Grid item xs={3}><Typography variant="caption" color="text.secondary">Customer</Typography><Typography variant="body2">{jobInfo.customer || '—'}</Typography></Grid>
                                <Grid item xs={3}><Typography variant="caption" color="text.secondary">Location</Typography><Typography variant="body2">{jobInfo.location || '—'}</Typography></Grid>
                                <Grid item xs={3}><Typography variant="caption" color="text.secondary">Manager</Typography><Typography variant="body2">{jobInfo.manager || '—'}</Typography></Grid>
                            </Grid>

                            {/* SOW Entries */}
                            {sowItems.length > 0 && sowItems.map((item, idx) => (
                                <React.Fragment key={item.id || idx}>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                                            {sowItems.length > 1 ? `SOW ${idx + 1}` : 'Scope of Work'}
                                        </Typography>
                                        {item.co && <Chip label={`CO ${item.co}`} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, backgroundColor: '#1565c0', color: '#fff' }} />}
                                        {item.scopeOfDate && <Typography variant="caption" color="text.secondary">{new Date(item.scopeOfDate).toLocaleDateString()}</Typography>}
                                    </Box>

                                    {(item.siteContact || item.phone) && (
                                        <Grid container spacing={1} sx={{ mb: 1 }}>
                                            {item.siteContact && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Contact</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{item.siteContact}</Typography></Grid>}
                                            {item.phone && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2" sx={{ fontSize: '0.85rem' }}><a href={`tel:${item.phone}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{item.phone}</a></Typography></Grid>}
                                        </Grid>
                                    )}

                                    {item.scopeOfWork && (
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Scope of Work</Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: '0.85rem' }}>{item.scopeOfWork}</Typography>
                                        </Box>
                                    )}

                                    {(item.reqdTools || item.reqdEquipment) && (
                                        <Grid container spacing={2} sx={{ mb: 1 }}>
                                            {item.reqdTools && (
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Required Tools</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: '0.85rem' }}>{item.reqdTools}</Typography>
                                                </Grid>
                                            )}
                                            {item.reqdEquipment && (
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Required Equipment</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: '0.85rem' }}>{item.reqdEquipment}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}

                                    {(item.proofTools || item.proofExpenses) && (
                                        <Grid container spacing={2} sx={{ mb: 1 }}>
                                            {item.proofTools && (
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Proof of Tools</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: '0.85rem' }}>{item.proofTools}</Typography>
                                                </Grid>
                                            )}
                                            {item.proofExpenses && (
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Proof of Expenses</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: '0.85rem' }}>{item.proofExpenses}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}

                                    {item.notes && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Notes</Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: '0.85rem' }}>{item.notes}</Typography>
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}

                            {/* SOW Documents */}
                            {documents.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                                        Documents ({documents.length})
                                    </Typography>
                                    <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                    <TableCell sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary' }}>Type</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary' }}>File</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', width: 70 }} align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {documents.map((doc) => {
                                                    const path = doc.docLocation || '';
                                                    const fileName = path.split(/[/\\]/).pop() || 'document';
                                                    const ext = fileName.split('.').pop()?.toLowerCase() || '';
                                                    const canPreview = isPreviewableExt(ext);
                                                    const isLoading = docLoadingId === doc.id;
                                                    const docDate = doc.docDate ? new Date(doc.docDate).toLocaleDateString() : '';
                                                    return (
                                                        <TableRow key={doc.id} hover>
                                                            <TableCell sx={{ py: 0.5 }}>
                                                                <Chip label={ext.toUpperCase() || 'FILE'} size="small" sx={{
                                                                    height: 18, fontSize: '0.6rem', fontWeight: 700,
                                                                    backgroundColor: ext === 'pdf' ? '#e53935' : ['xls', 'xlsx'].includes(ext) ? '#2e7d32' : ['doc', 'docx'].includes(ext) ? '#1565c0' : ['png', 'jpg', 'jpeg', 'tif', 'tiff', 'gif', 'bmp'].includes(ext) ? '#7b1fa2' : '#757575', color: '#fff'
                                                                }} />
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, maxWidth: 200 }}>
                                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }} noWrap>{fileName}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                                                                <Typography variant="caption" color="text.secondary">{docDate}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5 }} align="right">
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                                                                    {canPreview && (
                                                                        <IconButton size="small" color="primary" disabled={isLoading}
                                                                            onClick={() => handleDocAction(doc, true)} sx={{ p: 0.25 }}>
                                                                            {isLoading ? <CircularProgress size={14} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                        </IconButton>
                                                                    )}
                                                                    <IconButton size="small" disabled={isLoading} onClick={() => handleDocAction(doc, false)} sx={{ p: 0.25 }}>
                                                                        <DownloadIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                        </Box>
                    )}
                </Box>
            </Box>

            <PdfPreviewModal open={pdfPreviewOpen} onClose={() => { setPdfPreviewOpen(false); setPdfBlob(null); }}
                pdfBlob={pdfBlob} filename={pdfFilename} loading={pdfLoading} />
        </Box>
    );
};

export default SowForm;
